import { get, writable } from 'svelte/store';
import { checkForViewed, getDocumentId, getGamLineItemId, getGamCreativeId, getMuid, getOriginReferer, getRequestId, getRetailerByEnum, getSessionId, getSTax, getZipCodeQueryStringParam, flattenArrayByKey, getApiHost, getRetailerLogoUrl, isObjectEmpty, } from '../../../utils/index.js';
import { offersStore, logosStore, retailersCartCheckoutStore, RetailerPartnerType, CartType, Chains as KrogerChains, getChainKeyByName, getFormattedNameByKey, getGSIdByChainShortName, } from '../../../data-retailers/index.js';
import locationsStore from '../../../data-retailers/src/store/locations-store.js';
import { trackingStore } from '../../../data-tracking/index.js';
import { predictiveEndpoint, predictiveStore } from '../../../data-predictive/index.js';
import { convertCartSectionsToICartWithItemCollections, convertOffersCollectionsToICartItem, getSessionStoragePersistentCarts, getSessionStoragePersistentCartItems, setToSessionStorage, setSessionStoragePersistentCarts,
// getSessionStorageCartItems
 } from '../utils/index.js';
import { triggerPolarisTelemetryImpressionEvent, triggerPolarisTelemetryViewedEvent, triggerPolarisTelemetryComponentViewedEvent, triggerPolarisTelemetryUpdateZipCodeEvent, triggerPolarisTelemetryInvalidZipCodeEvent, triggerPolarisTelemetryNoStoresWithOffersNearByEvent, triggerPolarisTelemetryProductSelectedEvent, triggerPolarisTelemetryProductUnselectedEvent, updateTrackingParameter } from '../telemetry/index.js';
import { setSessionStoragePersistentCartRetailerLocation } from '../utils/session-storage.js';
let state = {};
const cart = writable(state);
const setValue = (key, value) => {
    state[key] = value;
    cart.set(state);
    //console.log('logged setValue', key, value);
};
cart.set({
    ...state,
    offersLoaded: false,
    currentScreen: 'main',
    displayedLocations: [],
    currentZip: '',
    activeLocationId: '',
    isKrogerGoToCartVisible: false,
    isCartFetchingData: true,
});
/**
 * `initializeLRSCartStore` is a specific implementation of the cart store initialization/setup.
 * All Carts (except for the LRS Cart) use the Predictive API to fetch Cart offers and locations data.
 * LRS Cart still uses the Products API.
 * @param {ICartConfig} `config` - the Cart configuration object
 */
const initializeLRSCartStore = async (sharedPkgVersion, config = {}) => {
    // Use passed in config object as cart state
    state = config.cart;
    setValue('isCartFetchingData', true);
    await retailersCartCheckoutStore.init({
        checkoutUrl: state.defaultFooterBtnUrl,
        type: CartType.INTEGRATED_RETAILER,
        retailer: state.retailer.name,
        currentCart: 0
    });
    await updateCheckoutCart(state.promotedProduct);
    setValue('isCartFetchingData', false);
    console.log('LRS Cart store initialized', state);
    await initPolarisTelemetry(config, sharedPkgVersion);
    triggerSharedCartComponentViewedTelemetryEvent();
};
const initSessionStoragePersistentCart = () => {
    const persistentCartsInSessionStorage = getSessionStoragePersistentCarts();
    const currentSessionStorageCart = {
        uuid: state.uuid,
        items: [],
        retailerLocation: state.storeLocation
    };
    if (!persistentCartsInSessionStorage.length) {
        console.log('No existing persistent carts found in sessionStorage.\nProceeding to add one...');
        setToSessionStorage('polPersistentCarts', [currentSessionStorageCart]);
        //console.log(getSessionStoragePersistentCarts());
        return;
    }
    console.log('Found existing persistent carts in sessionStorage.\nProceeding to check if current cart exists in sessionStorage...');
    const currentCartInSessionStorage = persistentCartsInSessionStorage.find(sessionStorageCart => sessionStorageCart.uuid === currentSessionStorageCart.uuid);
    if (!currentCartInSessionStorage) {
        //console.log(`Did not detect current cart uuid ${currentSessionStorageCart.uuid} in sessionStorage.\nProceeding to add it to "polPersistentCarts" sessionStorage array...`);
        // TO DO - ADD "currentCartInSessionStorage" to polPersistentCarts array in sessionStorage
        // This is to support MULTI PAGE PERSISTENT CART LOGIC
        console.log(getSessionStoragePersistentCarts());
        return;
    }
    console.log('Current cart was found in sessionStorage.\nProceeding to restore cart session...');
    console.log(getSessionStoragePersistentCarts());
    setValue('storeLocation', currentCartInSessionStorage.retailerLocation);
};
/**
 * Initializes the cart store with provided config or fetches cart data
 * @param cartConfig - Optional cart configuration object
 * @returns {Promise<void>} Promise that resolves when cart store is initialized
 */
const initializeCartStore = async (sharedPkgVersion, config) => {
    setValue('isCartFetchingData', true);
    if (config.type === 'lrs') {
        return initializeLRSCartStore(sharedPkgVersion, config);
    }
    // Re-initialize predictive store's endpoint for the shared Cart
    predictiveStore.init({ endpoint: `${getApiHost()}${predictiveEndpoint}cart/${config.cart.uuid}` });
    state = { ...state, ...config.cart };
    setValue('cartType', config.type);
    const zipCodeParam = getZipCodeQueryStringParam();
    setValue('currentZip', zipCodeParam ? zipCodeParam : config.cart.userLocation.zip);
    if (config.type === 'persistent') {
        initSessionStoragePersistentCart();
    }
    // Initialize retailer cart checkout store (the other cart-store)
    const retailersCartCheckoutStoreConfig = {
        checkoutUrl: state.defaultFooterBtnUrl,
        type: CartType.INTEGRATED_RETAILER,
        retailer: state.retailer ? state.retailer.name : RetailerPartnerType.NONE,
        currentCart: 0
    };
    await retailersCartCheckoutStore.init(retailersCartCheckoutStoreConfig);
    await initPolarisTelemetry(config, sharedPkgVersion);
    const isInitialComponentViewedEventTriggered = config.additionalTelemetry.isInitialComponentViewedTelemetryTriggered;
    updateMainCartScreen({ isComponentViewedEventTriggered: isInitialComponentViewedEventTriggered });
    // Fetch store locations
    // let storeLocations: IStoreLocation[] | [] = [];
    //storeLocations = state.storeLocation ? [state.storeLocation] : await fetchRetailerStoreLocations();
    // For now setDisplayedLocations will be an empty array when cart Store is initialized. 
    // Done so in order to force a locations fetch when change store is clicked (the first time)
    // Reason: PAU Carousel and standalone cart onfig provides only 1 store locations
    // While this is valid we should display all the locations in the area
    // If my memory is correct (DT) LRS will provide a full array preventing the need for a fetch
    // This is where cartType check might be useful but we can cross that bridge when we get to LRS
    setDisplayedLocations([]);
    //activeLocationIdis linked to the current location being checked in the (change store) displayedLocations array
    //setValue('activeLocationId', storeLocations.length ? storeLocations[0].id : '');
    //currentRetailer is linked to available items being checked on main screen
    //setValue('storeLocation', storeLocations.length ? storeLocations[0] : null);
    //Might not be needed
    //setValue('isInitialLocationsFetchCompleted', true);
    //The below might have been added for MXR - this will be false by default so no need to (re)set this
    //setValue('hideFooter', false);
    //Setting the current screen as the main screen - linked to cart header part
    setValue('currentScreen', 'main');
    setValue('isCartFetchingData', false);
    console.log('Cart initialized', state);
};
/**
 * Gets an array of products from section items
 * @param sectionItems - Array of section items to transform into products
 * @returns {IProduct[]} Array of products with their details and alternate options
 */
export const getSectionProducts = (sectionItems) => {
    const getAltProducts = (item) => {
        const altOffers = item.offers.slice(1);
        return altOffers.map((offer) => ({
            name: item.name,
            subhead: offer.name,
            imageSourcesArray: [{
                    url: offer.imageUrl,
                    density: 1
                }],
            price: offer.price,
            regularPrice: offer.regularPrice,
            ingredientId: `${item.baseItemId}`,
            id: offer.id,
        }));
    };
    return sectionItems.map((item) => ({
        name: item.name,
        subhead: item.offers.length ? item.offers[0].name : item.defaultProductName,
        imageSourcesArray: [{
                url: item.offers.length ? item.offers[0].imageUrl : item.defaultProductImageUrl,
                density: 1
            }],
        price: item.offers.length ? item.offers[0].price : item.defaultProductPrice,
        regularPrice: item.offers.length ? item.offers[0].regularPrice : '',
        ingredientId: item.baseItemId,
        id: item.offers.length ? item.offers[0].id : '',
        isDefault: item.offers.length ? false : true,
        isProductSwapEnabled: item.enableSwapping,
        altProducts: item.offers.length ? getAltProducts(item) : [],
        promotedProduct: isPromotedProduct(item.baseItemId),
        promotedId: fetchPromotedProduct(`${item.baseItemId}`)?.promotionId || ''
    }));
};
/**
 * Checks if any sections in the Cart have items
 * @param cartSections - Array of cart sections to check
 * @returns {boolean} True if any section contains items, False otherwise
 */
const sectionsHaveItems = (cartSections) => {
    return cartSections.some((section) => section.items && section.items.length > 0);
};
/** This function strictly controls what text is displayed in cart main screen footer
 * It will either be the retailer address or custom footer text.
 * It does not and should not control if the CTA checkout button is disabled
 */
const isCustomFooterTextDisplayed = () => {
    if (state.overrideRetailerLocationAddress) {
        return true;
    }
    if (state.storeLocation) {
        return false;
    }
    return true;
};
const getIsDefaultShown = () => {
    if (!state) {
        return true;
    }
    const isCurrentRetailerWithAddress = state && state.storeLocation && state.storeLocation.address
        ? false
        : true;
    // TO DO: update this logic for Target online-only offers determination
    return isCurrentRetailerWithAddress;
};
const initPolarisTelemetry = async (config, sharedPackageVersion) => {
    const updatedReporterConfig = {
        baseParams: {
            defaultShown: false, // Previously based on storeLocation === null Now setting as always false based on recent cart enhancements
            gamLineItemId: getGamLineItemId(window) || '',
            gamCreativeId: getGamCreativeId(window) || '',
            documentId: `${getDocumentId()}` || '',
            stax: getSTax(window) || '',
            sessionId: getSessionId() || '',
            requestId: getRequestId(window) || '',
            muid: getMuid(window) || '',
            originReferer: getOriginReferer() || '',
            appVersion: `v${sharedPackageVersion}`,
            retailers: [
                {
                    name: state.retailer ? state.retailer.name : 'Non Retailer Integrated',
                    chain: state.retailer && state.retailer.chain ? state.retailer.chain : '',
                    locationId: state.storeLocation ? `${state.storeLocation.id}` : ''
                }
            ]
        }
    };
    const { ['isInitialComponentViewedTelemetryTriggered']: _, ...additionalTelemetry } = config.additionalTelemetry;
    if (config.additionalTelemetry) {
        updatedReporterConfig.baseParams = {
            ...updatedReporterConfig.baseParams,
            ...additionalTelemetry
        };
    }
    await trackingStore.init(updatedReporterConfig);
    await triggerPolarisTelemetryImpressionEvent();
    await checkForViewed({
        targetId: 'app',
        onViewed: triggerPolarisTelemetryViewedEvent
    });
};
/**
 * Checks if a product ID matches a promoted product in state
 * @param offerProductId - The product ID to check
 * @returns True if the product ID matches a promoted product, false otherwise
 */
const isPromotedProduct = (offerProductId) => {
    if (!state.promotedProduct ||
        (state.promotedProduct && state.promotedProduct.length === 0)) {
        return false;
    }
    const promoFound = state.promotedProduct.find((promo) => Number(promo.ingredientId) === offerProductId);
    if (!promoFound)
        return false;
    return true;
};
/**
 * Finds and returns a promoted product from state that matches the given `baseItemId`
 * @param baseItemId - The ID of the base item to search for
 * @returns The matching promoted product or undefined if not found
 */
const fetchPromotedProduct = (baseItemId) => {
    return state.promotedProduct?.find((promo) => `${promo.ingredientId}` === `${baseItemId}`);
};
const fetchDataFromProductsAPI = async () => {
    try {
        setValue('currentScreen', 'main');
        const cartItemDefaults = state.sections[0].items.map((item) => {
            return {
                baseItemId: item.baseItemId,
                defaultProductImageUrl: item.defaultProductImageUrl,
                defaultProductName: item.defaultProductName,
                defaultProductPrice: item.defaultProductPrice,
                name: item.name
            };
        });
        await getOffersDataFromProductsAPI();
        const offersCollections = get(offersStore).collections['cart'];
        // Convert offersCollections into ICartItem[]
        const newCartItems = convertOffersCollectionsToICartItem(offersCollections, cartItemDefaults);
        // Save the newCartItems to the items array
        const newSections = [{
                ...state.sections[0],
                items: newCartItems
            }];
        // Save updated sections
        setValue('sections', newSections);
        trackingStore.updateReporter({
            baseParams: {
                ...trackingStore.getBaseParams(),
                retailers: [
                    {
                        name: state.retailer.name,
                        chain: state.retailer.chain || '',
                        locationId: `${state.retailer.id}` || ''
                    }
                ]
            }
        });
        setValue('offersLoaded', true);
    }
    catch (error) {
        console.warn(error);
    }
};
const getLocationData = async (zipCode, isSourceZipcodeIntakeForm = false) => {
    if (state.cartType === 'lrs') {
        return getLocationDataFromProductsAPI(zipCode, isSourceZipcodeIntakeForm);
    }
    try {
        console.log("attempting new fetchRetailerStoreLocations");
        const storeLocations = await fetchRetailerStoreLocations(zipCode);
        //console.log(storeLocations)
        setValue("displayedLocations", storeLocations);
        setValue('currentZip', isSourceZipcodeIntakeForm && zipCode ? zipCode : state.currentZip);
        if (isSourceZipcodeIntakeForm) {
            // An "invalid" zip code (e.g., '91000') will no longer return an error and instead return '{}', so we need to check if the response is an empty object
            if (isObjectEmpty(storeLocations)) {
                return triggerPolarisTelemetryInvalidZipCodeEvent();
            }
            // A valid zip code that returns no stores will trigger a "no stores near you" event
            // Example: Kroger + '50309' == no stores
            if (!storeLocations.length) {
                return triggerPolarisTelemetryNoStoresWithOffersNearByEvent();
            }
            // A valid zip code that returns stores will trigger an "update zip code" event
            if (storeLocations.length) {
                return triggerPolarisTelemetryUpdateZipCodeEvent();
            }
        }
    }
    catch (error) {
        console.warn('Unable to fetch locations for zip code: ', zipCode);
        if (isSourceZipcodeIntakeForm) {
            triggerPolarisTelemetryInvalidZipCodeEvent();
        }
    }
};
const getLocationDataFromProductsAPI = async (zipCode, isSourceZipcodeIntakeForm = false) => {
    try {
        const shouldFetchLocationsImmediately = false; //sessionLocations.length < 10;
        const maxStoreLocations = 10; //state.maxStorePickerAddresses;
        await logosStore.init();
        await locationsStore.init({
            locations: [],
            maxStoreLocations: maxStoreLocations,
            retailerIntegration: state.retailer.name
        }, shouldFetchLocationsImmediately);
        await locationsStore.fetchLocations(maxStoreLocations, zipCode);
        setValue('currentZip', get(locationsStore).requestedLocationObject.zip);
        const storeLocations = get(locationsStore).locations || [];
        const convertedStoreLocations = storeLocations.map((loc) => {
            return {
                id: loc.id,
                name: (loc.chain ? getFormattedNameByKey(loc.chain) : formatRetailerName()),
                address: loc.address.street,
                city: loc.address.city,
                state: loc.address.state,
                zipCode: loc.address.zip,
                distance: loc.address.distance
            };
        });
        setDisplayedLocations(convertedStoreLocations);
        setValue('activeLocationId', state.storeLocation ? state.storeLocation.id : '');
        if (isSourceZipcodeIntakeForm) {
            if (!storeLocations.length) {
                return triggerPolarisTelemetryNoStoresWithOffersNearByEvent();
            }
            return triggerPolarisTelemetryUpdateZipCodeEvent();
        }
    }
    catch (error) {
        console.warn('Unable to fetch locations for zip code: ', zipCode);
        if (isSourceZipcodeIntakeForm) {
            setValue('currentZip', '');
            setDisplayedLocations([]);
            setValue('activeLocationId', '');
            triggerPolarisTelemetryInvalidZipCodeEvent();
        }
    }
};
const initOffersStore = async () => {
    try {
        const retailerIntegration = state.retailer.name;
        const collectionsInit = [];
        const activeProductIds = [];
        const cartItems = flattenArrayByKey(state.sections, 'items');
        console.log("cartItems", cartItems);
        cartItems.forEach((item) => {
            activeProductIds.push(item.baseItemId);
        });
        collectionsInit.push({
            id: 'cart',
            productIds: activeProductIds
        });
        await offersStore.init({ retailerIntegration }, collectionsInit);
    }
    catch (error) {
        console.warn('offersStore was unable to initialize.');
    }
};
const getOffersDataFromProductsAPI = async () => {
    try {
        initOffersStore();
        await offersStore.fetchOffersForId('cart', `${state.storeLocation?.id}`);
    }
    catch (error) {
        console.warn('offersStore was unable to fetchOffersForId.');
    }
};
const setDisplayedLocations = (locationsToSet) => {
    setValue('displayedLocations', locationsToSet);
};
const updateCheckoutCart = async (promotedProduct) => {
    const getCartSections = () => {
        const isItemPromotedProduct = (currentItem, promoProduct) => {
            if (!promoProduct || !promoProduct.length || isObjectEmpty(currentItem)) {
                return false;
            }
            return `${currentItem.baseItemId}` === `${promoProduct[0].ingredientId}`;
        };
        // Converts Cart sections into ICheckoutCartSection format
        const convertedSections = state.sections.map((section) => {
            return {
                title: section.title,
                itemCollections: section.items.map((item) => convertCartSectionsToICartWithItemCollections(item, isItemPromotedProduct(item, promotedProduct), section.defaultChecked))
            };
        });
        // console.log('Log from function updateCheckoutCart:\nconvertedSections:', convertedSections);
        return convertedSections;
    };
    // Get current Cart sections into ICheckoutCartSection format
    const cartSections = getCartSections();
    // Check against sessionsStorage to keep isSelected state
    if (state.cartType === 'persistent') {
        const sessionStorageCartItems = getSessionStoragePersistentCartItems(state.uuid);
        if (sessionStorageCartItems) {
            //console.log("found sessionStorageCartItems during updateCheckoutCart:", sessionStorageCartItems);
            cartSections.forEach(section => {
                section.itemCollections.forEach(itemInItemCollections => {
                    sessionStorageCartItems.forEach(sessionStorageCartItem => {
                        if (sessionStorageCartItem.baseIngredientId.toString() === itemInItemCollections.productId.toString()) {
                            itemInItemCollections.isSelected = (sessionStorageCartItem.isIntendedToBeSelected);
                            // Persistent cart needs an extra safety check
                            // Even if a user did "add to cart" we need to ensure it has offers
                            const isItemPriceInvalid = isNaN(Number(itemInItemCollections.items[0].price));
                            if (isItemPriceInvalid) {
                                itemInItemCollections.isSelected = false;
                            }
                            //console.log("updateCheckoutCart - itemInItemCollections:", itemInItemCollections);
                        }
                    });
                });
            });
        }
        //console.log("Inside updateCheckoutCart function...\nitemCollections:", cartSections, "\nsessionStorageCartItems:", sessionStorageCartItems)
    }
    // Update the retailer cart checkout store with desired cart sections
    retailersCartCheckoutStore.setValue('carts', [
        { id: `cart`, cartSections: cartSections }
    ]);
    //console.log("Current Selected Items:", retailersCartCheckoutStore.getCurrentSelectedItems())
    if (state.cartType === 'persistent') {
        //console.log('Sending a postMessage from the cart iframe to parent page');
        //console.log('Telling it to set selected item count in the cart button:', getSelectedCartitems().length);
        //console.log(retailersCartCheckoutStore.getCurrentSelectedItems())
        window.parent.postMessage({
            type: 'setCartItemCount',
            itemCount: getSelectedCartitems().length
        }, '*');
        setSessionStoragePersistentCartRetailerLocation(state.uuid, state.storeLocation);
        console.log(getSessionStoragePersistentCarts());
    }
    //console.log(retailersCartCheckoutStore.getCurrentSelectedItems())
};
/**
 * Invokes the data-retailers `checkout()` function, which in turn calls the appropriate retailer checkout service.
 * Passes the current retailer, impact radius data, and a callback to toggle Kroger's "Go to Cart" visibility.
 */
const checkoutCart = async () => {
    retailersCartCheckoutStore.checkout(state.storeLocation, getImpactRadius(), state.retailer?.chain);
};
/**
 * Returns the `impactRadius` object from state if it exists, otherwise returns a default object with `subId1`, `subId2`, and `subId3` properties.
 *
 * state.impactRadius would only exist if the cartConfig was provided one from the start (LRS) otherwise it is undefined
 *
 * impactRadius is only use used for Walmart and Target (not Kroger or its subchains)
 *
 * The "default" impactRadius values are what stand alone cart uses. They are different from LRS
 *
 * More details here: https://dotdash.atlassian.net/browse/INN-2082
 * @returns {Object} - The `impactRadius` object.
 */
const getImpactRadius = () => {
    if (!state.retailer) {
        return undefined;
    }
    if (state.retailer.name === "kroger") {
        return undefined;
    }
    // If Impact Radius info comes from the parent app, use that state object.
    if (state.impactRadius) {
        return state.impactRadius;
    }
    return {
        subId1: `${getRetailerByEnum(state.retailer.name)?.toUpperCase() || ''}`, // TGT or WMT
        subId2: `${state.name || ''}`, //Returns the cart name
        subId3: `${getSessionId() || ''}` // Returns parent page dataLayer sessionId if available or time stamp - same one that is in the POL telemetry events
    };
};
const toggleKrogerGoToCartVisibility = () => {
    setValue('isKrogerGoToCartVisible', !state.isKrogerGoToCartVisible);
};
const triggerPersistentCartItemSelected = async (recievedItem) => {
    // console.log("attempting to triggerPersistentCartItemSelected", recievedItem);
    let cartItemSectionIndex = -1;
    let cartItemIndex = -1;
    state.sections.forEach((section, sectionIndex) => {
        section.items.forEach((item, itemIndex) => {
            if (item.baseItemId === recievedItem.baseIngredient.id) {
                cartItemSectionIndex = sectionIndex;
                cartItemIndex = itemIndex;
            }
        });
    });
    if (cartItemSectionIndex < 0 || cartItemIndex < 0) {
        /*console.log(
            `Item recieved via post message not found in any cart sections`,
            "\n\nCart Config Sections:\n", state.sections,
            "\n\nitem recieved via post message:\n", recievedItem,
            "\n\ncartItemSectionIndex:\n", cartItemSectionIndex,
            "\n\ncartItemIndex:\n", cartItemIndex
        );*/
        return;
    }
    updateSelectedCartItem(cartItemSectionIndex, cartItemIndex, state.sections[cartItemSectionIndex].items[cartItemIndex]);
};
/**
 * Updates the selected state of a checkoutCart item and triggers the appropriate select/unselect telemetry event.
 * @param {number} cartSectionIndex - The index of the cart section containing the item.
 * @param {number} cartItemIndex - The index of the cart item within its section.
 * @param {Object} itemSelected - The item being selected or unselected.
 */
const updateSelectedCartItem = (cartSectionIndex, cartItemIndex, itemSelected) => {
    //console.log("cartSectionIndex:", cartSectionIndex);
    //console.log("cartItemIndex:", cartItemIndex);
    //console.log(itemSelected);
    //console.log("CurrentSelectedItems:", retailersCartCheckoutStore.getCurrentSelectedItems());
    //console.log("Attempting to toggleItemSelected and update the retailersCartCheckoutStore...");
    retailersCartCheckoutStore.toggleIsItemSelected(cartSectionIndex, cartItemIndex);
    //console.log("CurrentSelectedItems:", retailersCartCheckoutStore.getCurrentSelectedItems());
    //console.log(retailersCartCheckoutStore.isCurrentItemSelected(cartSectionIndex, cartItemIndex))
    // Fire off the appropriate Telemetry event based on whether the product is already selected or not
    retailersCartCheckoutStore.isCurrentItemSelected(cartSectionIndex, cartItemIndex)
        ? triggerPolarisTelemetryProductSelectedEvent(itemSelected)
        : triggerPolarisTelemetryProductUnselectedEvent(itemSelected);
};
const getSelectedCartitems = () => {
    return retailersCartCheckoutStore.getCurrentSelectedItems();
};
/**
 * Function to call the changeItemForProduct() to swap selected product in the checkoutCartStore
 * @returns void
 */
const updateSwappedCartItem = (productId, productIndex, currentCartSectionIndex) => {
    retailersCartCheckoutStore.changeItemForProduct(productId, productIndex, currentCartSectionIndex);
};
const updateMainCartScreen = async (mainCartScreenConfig) => {
    setValue('isCartFetchingData', true);
    //console.log("updateMainCartScreen:", mainCartScreenConfig)
    const cartConfig = mainCartScreenConfig.isDataFetchNeeded ? await fetchCartData(mainCartScreenConfig.storeLocation.id) : state;
    const retailerInfo = cartConfig.retailer;
    const cartFooterLogoUrl = cartConfig.footerLogoUrl || (retailerInfo ? getRetailerLogoUrl(retailerInfo.logos, cartConfig.logoFormat) : '');
    setValue("storeLocation", cartConfig.storeLocation);
    setValue('activeLocationId', cartConfig.storeLocation ? cartConfig.storeLocation.id : '');
    setValue("sections", cartConfig.sections);
    setValue("retailer", retailerInfo);
    setValue("footerLogoUrl", cartFooterLogoUrl);
    // Updating the retailer cart checkout store with the latest cart item details (price, ids, total, etc.)
    await updateCheckoutCart();
    await updateTrackingParameter({
        retailers: [{
                name: retailerInfo ? retailerInfo.name : 'Non Retailer Integrated',
                chain: retailerInfo ? retailerInfo.chain : '',
                locationid: cartConfig.storeLocation ? `${cartConfig.storeLocation.id}` : ''
            }]
    });
    if (mainCartScreenConfig.isComponentViewedEventTriggered) {
        triggerSharedCartComponentViewedTelemetryEvent();
    }
    setValue('isCartFetchingData', false);
    setValue('currentScreen', 'main');
};
const updateCartWithProductsAPIData = async (newStore) => {
    setValue('isCartFetchingData', true);
    const isKrogerCart = state.retailer.name === 'kroger';
    const matchedKrogerChain = getChainKeyByName(newStore.name);
    const krogerChainWasChanged = isKrogerCart && state.retailer.chain !== matchedKrogerChain;
    // Update storeLocation
    setValue('storeLocation', newStore);
    const newRetailer = {
        id: state.retailer.id, // remains the same parent retailer ID
        name: state.retailer.name, // remains the same parent retailer
        chain: isKrogerCart ? matchedKrogerChain : '',
        logo: '',
        logos: {
            squareUrl: '',
            rectangleUrl: '',
            whiteSquareUrl: '',
            whiteRectangleUrl: ''
        },
        integrated: true,
    };
    // Update retailer
    setValue('retailer', newRetailer);
    // If Kroger chain changed, update footer logo and default footer button URL
    if (krogerChainWasChanged) {
        await updateKrogerChainFooterLogo(matchedKrogerChain);
        setValue('defaultFooterBtnUrl', `${KrogerChains.get(`${matchedKrogerChain}`)?.Url || 'https://www.kroger.com'}`);
    }
    await fetchDataFromProductsAPI();
    await updateCheckoutCart(state.promotedProduct);
    // The above fetchDataFromProductsAPI() call is made after "Use This Store" click
    // (and we might have initially loaded default Cart data, or we are now selecting a store location that returns 0 offers for the cart products), so...
    // we should check if defaultShown tracking parameter needs to be updated
    if (trackingStore.getBaseParams()?.defaultShown !== getIsDefaultShown()) {
        await updateTrackingParameter({ defaultShown: getIsDefaultShown() });
    }
    triggerSharedCartComponentViewedTelemetryEvent();
    setValue('isCartFetchingData', false);
};
const updateKrogerChainFooterLogo = async (newRetailer) => {
    const retailerId = getGSIdByChainShortName(newRetailer);
    await logosStore.fetchRetailersLogos([retailerId]);
    const newRetailersLogos = get(logosStore).retailerLogos[0];
    // Update retailer logos in store
    setValue('retailer', {
        ...state.retailer,
        logos: {
            squareUrl: newRetailersLogos.squareUrl,
            rectangleUrl: newRetailersLogos.rectangleUrl,
            whiteSquareUrl: newRetailersLogos.whiteSquareUrl,
            whiteRectangleUrl: newRetailersLogos.whiteRectangleUrl
        }
    });
    setValue('footerLogoUrl', newRetailersLogos.squareUrl);
};
const formatRetailerName = () => {
    if (!state)
        return; // Needed for Carts rendered via Modal
    if (!state.retailer)
        return;
    if (state.retailer && state.retailer.name === RetailerPartnerType.KROGER) {
        return getFormattedNameByKey(state.retailer.chain || state.retailer.name);
    }
    if (state.retailer.name === RetailerPartnerType.WALMART ||
        state.retailer.name === RetailerPartnerType.TARGET) {
        const retailer = state.retailer.name;
        return retailer[0].toUpperCase() + retailer.slice(1).toLowerCase();
    }
};
/**
 * Fetches cart data from the predictive store.
 * NOTE: the store picker safety check has been removed; this check shall occur wherever the function is called.
 * If a store ID is provided, fetches data for that specific store.
 * Otherwise, fetches default cart data.
 * @param storeId - Optional store identifier
 * @returns Promise containing the cart data
 */
const fetchCartData = async (storeId = '') => {
    if (storeId) {
        return await predictiveStore.getPredictiveCartData(storeId);
    }
    return await predictiveStore.getPredictiveCartData();
};
/**
 * Fetches store locations for the predictive cart.
 * NOTE: ensure enableStorePicker is true before calling this function; access error may occur otherwise.
 * @returns Promise containing the store locations data
 */
const fetchRetailerStoreLocations = async (zipCode) => {
    return await predictiveStore.getPredictiveCartStoreLocations(zipCode);
};
const triggerSharedCartComponentViewedTelemetryEvent = () => {
    triggerPolarisTelemetryComponentViewedEvent(getSectionProducts(flattenArrayByKey(state.sections, 'items')));
};
export default {
    subscribe: cart.subscribe,
    set: cart.set,
    setValue,
    getLocationDataFromProductsAPI,
    getLocationData,
    getSelectedCartitems,
    formatRetailerName,
    checkoutCart,
    initializeCartStore,
    isCustomFooterTextDisplayed,
    updateSelectedCartItem,
    updateSwappedCartItem,
    setDisplayedLocations,
    toggleKrogerGoToCartVisibility,
    updateCartWithProductsAPIData,
    updateCheckoutCart,
    updateMainCartScreen,
    initPolarisTelemetry,
    getIsDefaultShown,
    locationsStore,
    fetchCartData,
    sectionsHaveItems,
    getSectionProducts,
    triggerSharedCartComponentViewedTelemetryEvent,
    triggerPersistentCartItemSelected,
    isPromotedProduct,
    fetchPromotedProduct
};
